import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import HorizontalBarChartGraph from "../../../components/common/Charts/HorizontalBarChartGraph";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import InputSwitch from "../../../components/common/Inputs/InputSwitch/InputSwitch";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { useMoneda } from "../../../context/MonedaContext";
import { simboloMoneda } from "../../../utils/moneda";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import { Field } from "../../../components/ui-kitNew/controlsNew";
import { createFilterState } from "../../../lib/reportNew/filter-stateNew";
import { esChatarra } from "../../../lib/informes/clasificacionResiduos";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import {
    INDICADORES,
    QUICK_FILTERS_INDICADOR,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
} from "../indicadores/definiciones";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./AbonoChatarra.scss";

const INDICADOR = INDICADORES.abonoChatarra;

export default function AbonoChatarra() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [datos, setDatos] = useState([]);
    const [vista, setVista] = useState("euros");

    const { selectedZone } = useZone();
    const { moneda } = useMoneda();

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: () =>
                vista === "euros"
                    ? "api/Informe/CreateReportExcelAbonoChatarraResumen"
                    : "api/Informe/CreateReportExcelAbonoChatarraPorcentajeResumen",
        });

    const { filterFields } = INDICADOR;

    const [filterState, setFilterState] = useState(() =>
        createFilterState(QUICK_FILTERS_INDICADOR)
    );

    const [paisesArr, setPaisesArr] = useState([]);
    const [tiposObraArr, setTiposObraArr] = useState([]);

    /* Se vuelve a pedir al cambiar de moneda: la conversión la hace el backend. */
    useEffect(() => {
        if (!moneda) return;

        const fetchDatos = async () => {
            setLoading(true);
            try {
                const api = getApiInstance();
                const response = await api.get(INDICADOR.datosUrl(moneda));
                setDatos(INDICADOR.filasDe(response.data));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDatos();
    }, [moneda]);

    useEffect(() => {
        const api = getApiInstance();

        const fetchPaises = async () => {
            try {
                const response = await api.get(`/api/Paises/GetPaises`);
                setPaisesArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaises();

        const fetchTiposObra = async () => {
            try {
                const response = await api.get(
                    `/api/ObrasTipo/GetAllObrasTipo`
                );
                setTiposObraArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchTiposObra();
    }, []);

    const mapPaises = useMemo(() => mapaPaises(paisesArr), [paisesArr]);
    const mapTiposObra = useMemo(
        () => mapaTiposObra(tiposObraArr),
        [tiposObraArr]
    );
    const reversePaises = useMemo(
        () =>
            Object.fromEntries(
                paisesArr.map((p) => [String(p.pais), String(p.codPais)])
            ),
        [paisesArr]
    );
    const reverseTiposObra = useMemo(
        () =>
            Object.fromEntries(
                tiposObraArr.map((t) => [
                    String(t.descripcion),
                    String(t.codTipoObra),
                ])
            ),
        [tiposObraArr]
    );

    const datosNormalizados = useMemo(
        () => normalizarFilas(datos, mapPaises, mapTiposObra),
        [datos, mapPaises, mapTiposObra]
    );

    // Esta pantalla no tiene el checkbox de RAAES / Zero Waste.
    const datosFiltrados = useMemo(
        () => filasVisibles(INDICADOR, datosNormalizados, filterState, false),
        [datosNormalizados, filterState]
    );

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        const paises = Array.isArray(quick.pais) ? quick.pais : [];
        const tipos = Array.isArray(quick.tipoObra) ? quick.tipoObra : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedPais: paises.length
                    ? reversePaises[paises[0]] ?? paises[0]
                    : "all",
                selectedTipoObra: tipos.map((t) => reverseTiposObra[t] ?? t),
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las filas que se están viendo. Los filtros
               de arriba se quedan por compatibilidad. */
            codsFila: codsFilaDe(datosFiltrados),
        };
    }, [filterState, reversePaises, reverseTiposObra, datosFiltrados]);

    const { barras, promedio, hasData } = useMemo(() => {
        // Todas las obras con algun residuo de codigo LER de chatarra, tengan
        // o no abono cobrado: las que no tienen nada abonado salen con barra
        // a 0 para poder detectarlas sin tener que mirar el grafico de %.
        const conChatarra = datosFiltrados.filter(esChatarra);

        // Suma por obra
        const porObra = {};
        conChatarra.forEach((item) => {
            const codObra = item.codObra;
            if (codObra == null) return;
            if (!porObra[codObra]) porObra[codObra] = 0;
            porObra[codObra] += Number(item.abonoChatarra) || 0;
        });

        const totalAbonado = Object.values(porObra).reduce(
            (sum, v) => sum + v,
            0
        );
        const numObras = Object.keys(porObra).length;

        const nombreObra = (codObra) => {
            const o =
                datosFiltrados.find((item) => item.codObra === codObra) || {};
            return (
                o.codEspacio ||
                o.codIntegracion ||
                o.nombre ||
                `Obra ${codObra}`
            );
        };

        const barras = Object.entries(porObra)
            .map(([codObra, value]) => ({
                name: nombreObra(codObra),
                value: parseFloat(Math.abs(value).toFixed(2)),
            }))
            .sort((a, b) => b.value - a.value);

        return {
            barras,
            promedio: numObras > 0 ? Math.abs(totalAbonado) / numObras : 0,
            hasData: numObras > 0,
        };
    }, [datosFiltrados, esChatarra]);

    /* Estado por obra: abonada solo si TODOS sus residuos de chatarra tienen abono
       distinto de 0; parcial si solo algunos; sin abono si ninguno. Al porcentaje
       solo suman las totalmente abonadas, igual que en la plataforma antigua y que
       el EstadoAbono del Excel.

       El "distinto de 0" es imprescindible: ResiduosObra.AbonoChatarra tiene
       DEFAULT 0, nunca queda a NULL, asi que comprobar solo != null daba por
       abonadas todas las obras y el indicador salia 100% en todos los paises. */
    const { pieData, porcentajeObrasConAbono } = useMemo(() => {
        const conChatarra = datosFiltrados.filter(esChatarra);

        const residuosPorObra = new Map();
        conChatarra.forEach((item) => {
            const codObra = item.codObra;
            if (codObra == null) return;
            if (!residuosPorObra.has(codObra)) {
                residuosPorObra.set(codObra, { total: 0, conAbono: 0 });
            }
            const acc = residuosPorObra.get(codObra);
            acc.total += 1;
            if (item.abonoChatarra != null && Number(item.abonoChatarra) !== 0) {
                acc.conAbono += 1;
            }
        });

        let totalConAbono = 0;
        let parciales = 0;
        let sinAbono = 0;

        residuosPorObra.forEach(({ total, conAbono }) => {
            if (conAbono === 0) sinAbono += 1;
            else if (conAbono === total) totalConAbono += 1;
            else parciales += 1;
        });

        const totalConChatarra = residuosPorObra.size;
        const porcentaje = (n) =>
            totalConChatarra > 0 ? Number(((n / totalConChatarra) * 100).toFixed(2)) : 0;
        const pct =
            totalConChatarra > 0 ? (totalConAbono / totalConChatarra) * 100 : 0;

        return {
            pieData: [
                {
                    name: "Con abono de chatarra",
                    value: porcentaje(totalConAbono),
                },
                {
                    name: "Abono parcial",
                    value: porcentaje(parciales),
                },
                {
                    name: "Sin abono de chatarra",
                    value: porcentaje(sinAbono),
                },
            ],
            porcentajeObrasConAbono: pct,
        };
    }, [datosFiltrados, esChatarra]);

    const formatPromedio = (valor) =>
        new Intl.NumberFormat("es-ES", {
            maximumFractionDigits: 2,
        }).format(valor);

    // Los importes llegan ya convertidos a la moneda del selector.
    const simbolo = simboloMoneda(moneda);

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={datosNormalizados}
                state={filterState}
                onChange={setFilterState}
                resultCount={datosFiltrados.length}
                totalCount={datosNormalizados.length}
                fields={filterFields}
                headerExtra={
                    <Field label="Vista">
                        <div
                            className="abono-toggle"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                            }}
                        >
                            <span
                                className={`abono-toggle__label ${
                                    vista === "euros"
                                        ? "abono-toggle__label--active"
                                        : ""
                                }`}
                            >
                                {simbolo} abono / obra
                            </span>
                            <InputSwitch
                                checked={vista === "porcentaje"}
                                onChange={(isOn) =>
                                    setVista(isOn ? "porcentaje" : "euros")
                                }
                            />
                            <span
                                className={`abono-toggle__label ${
                                    vista === "porcentaje"
                                        ? "abono-toggle__label--active"
                                        : ""
                                }`}
                            >
                                % obras con abono
                            </span>
                        </div>
                    </Field>
                }
                excludeFields={INDICADOR.excludeFields}
            />
        </div>
    );

    return (
        <div className="abono-chatarra-page">
            <PageLayout
                title="Informe de Abono de Chatarra"
                subtitle={`${simbolo} abonados por obra con chatarra`}
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="tab-content-with-filters">
                    {renderFilters()}

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="charts-section">
                                <div className="chart-container pie-chart-container">
                                    <div className="chart-header valorizacion-header">
                                        <div className="valorizacion-header__left">
                                            {vista === "euros" ? (
                                                <>
                                                    <h3>
                                                        {simbolo} abono chatarra / obra
                                                    </h3>
                                                    <p className="abono-formula">
                                                        Códigos LER: 150104,
                                                        170401, 170402, 170405,
                                                        170407, 170411, 191202,
                                                        200140, y en México los
                                                        metales — incluye obras
                                                        sin abono (0{simbolo})
                                                    </p>
                                                    <p className="abono-formula">
                                                        {simbolo} abonados / obra = Σ {simbolo}
                                                        abonados en obras con
                                                        chatarra / Nº obras con
                                                        chatarra.
                                                    </p>
                                                </>
                                            ) : (
                                                <>
                                                    <h3>
                                                        % obras con abono de
                                                        chatarra
                                                    </h3>
                                                    <p className="abono-formula">
                                                        Sobre obras con alguno
                                                        de los códigos LER de
                                                        chatarra (150104,
                                                        170401, 170402, 170405,
                                                        170407, 170411, 191202,
                                                        200140), y en México con
                                                        metales
                                                    </p>
                                                    <p className="abono-formula">
                                                        % obras con abono = Nº
                                                        obras con chatarra y
                                                        abono / Nº obras con
                                                        chatarra
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                        <div className="abono-percentage">
                                            {vista === "euros" ? (
                                                <>
                                                    <span className="abono-percentage__value">
                                                        {hasData
                                                            ? formatPromedio(
                                                                  promedio
                                                              )
                                                            : "—"}
                                                    </span>
                                                    <span className="abono-percentage__unit">
                                                        {simbolo}/obra
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="abono-percentage__value">
                                                        {hasData
                                                            ? formatPromedio(
                                                                  porcentajeObrasConAbono
                                                              )
                                                            : "—"}
                                                    </span>
                                                    <span className="abono-percentage__unit">
                                                        %
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="chart-wrapper">
                                        {loading ? (
                                            <div
                                                className="informe-chart-skeleton informe-chart-skeleton--bar"
                                                aria-hidden="true"
                                            >
                                                <div className="informe-chart-skeleton__bars">
                                                    {[80, 55, 70, 40, 62, 30, 48].map(
                                                        (h, i) => (
                                                            <div
                                                                key={i}
                                                                className="informe-chart-skeleton__bar"
                                                                style={{
                                                                    height: `${h}%`,
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        ) : vista === "porcentaje" ? (
                                            <PieChartGraph
                                                data={pieData}
                                                colors={[
                                                    "#10b981",
                                                    "#f5803e",
                                                    "#b0bec5",
                                                ]}
                                                height={400}
                                            />
                                        ) : hasData ? (
                                            <HorizontalBarChartGraph
                                                data={barras}
                                                height={480}
                                                simbolo={simbolo}
                                            />
                                        ) : (
                                            <div className="abono-empty">
                                                <div className="abono-empty__icon">
                                                    <span />
                                                    <span />
                                                    <span />
                                                </div>
                                                <p className="abono-empty__text">
                                                    No hay datos de abono de
                                                    chatarra para los filtros
                                                    seleccionados.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Abono de Chatarra"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>
            </PageLayout>
        </div>
    );
}
